import { inject, Injectable } from '@angular/core';
import { map, Observable, of, catchError } from 'rxjs';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, Router, CanActivateFn } from '@angular/router';
import { AuthService } from './auth.service';
import { OutlineService } from './outline.service';
import { NavigationLoaderService } from '../navigation/navigation-loader.service';
import Swal from 'sweetalert2';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard implements CanActivate {
  constructor(
    private authService: AuthService,
    private router: Router,
    private outlineService: OutlineService,
    private navigationLoaderService: NavigationLoaderService
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    // Verificar si el usuario está autenticado
    if (!this.authService.isAuthenticated()) {
      if (state.url !== '/login') {
        this.router.navigate(['/login'], { queryParams: { returnUrl: state.url } });
      }
      return of(false);
    }

    if (!localStorage.getItem('auth_user') && state.url !== '/login') {
      this.router.navigate(['/login']);
      return of(false);
    }

    const id_modulo = route.data['id_modulo'];
    const id_vista = route.data['id_vista'];

    // Si no se requieren permisos especiales para esta ruta, permitir acceso
    if (!id_modulo || !id_vista) {
      return of(true);
    }

    const perfilId = this.authService.getCurrentUser()?.perfil_id;

    if (!perfilId) {
      this.router.navigate(['/login']);
      return of(false);
    }

    return this.outlineService.cargarPermisos(perfilId).pipe(
      map(() => {
        const tienePermisoBloquear = this.outlineService.tienePermiso(id_modulo, id_vista, 'Bloquear');
        const tienePermisoOcultar = this.outlineService.tienePermiso(id_modulo, id_vista, 'Ocultar');

        if (!tienePermisoBloquear || !tienePermisoOcultar) {
          Swal.fire({
            title: 'Acceso denegado',
            text: 'No tienes permisos para acceder a esta vista',
            imageUrl: 'assets/img/icons/logos/bloquear1.png',
            imageWidth: 100,
            imageHeight: 100,
            imageAlt: 'Icono de candado',
            timer: 3000,
            timerProgressBar: true,
            showConfirmButton: false
          });
          return false;
        }

        // Cargar elementos UI para directivas (fire and forget)
        this.outlineService.cargarElementos(perfilId).subscribe();

        // Actualizar visibilidad del menú con los permisos recién cargados
        this.navigationLoaderService.refreshNavigation();

        return true;
      }),
        catchError(() => {
          // Si falla cargar permisos, no cerrar sesión — permitir o redirigir a home
          this.router.navigate(['/cic-home/home']);
          return of(false);
        })  
    );
  }
}

export const authGuard: CanActivateFn = (route, state) => {
  return inject(AuthGuard).canActivate(route, state);
};
